import { MaintenanceWindowData } from '../../../configurationProfilesApp/maintenanceWindowForm/types';
import { MaintenanceWindowPatchSpecificsData } from '../../../configurationProfilesApp/maintenanceWindowFormPatchSpecifics/types';
import { PostponeRebootOptions } from '../../../types/common/maintenanceWindowCommonTypes';
import { MaintenanceWindowRebootOptionsData } from '../../../maintenanceWindows/maintenanceWindowRebootOptions/types';
import { MaintenanceWindowSimpleSchedulerSchema } from '../../../maintenanceWindows/maintenanceWindowSimpleScheduler/types';
import {
  MaintenanceWindowSchedulerSchema,
  MonthlyRecurrence,
  WeeklyRecurrence,
} from '../../../maintenanceWindows/maintenanceWindowScheduler/types';
import { MaintenanceWindowsConstants } from '../../../maintenanceWindows/MaintenanceWindowsConstants';
import { RecurrenceOptions } from '../../../types/common/schedulerTypes';

export class DbToUiValuesConverter {
  public static prepareUiData(dbData: any): MaintenanceWindowData {
    const specifics: MaintenanceWindowPatchSpecificsData = {
      scanForPatches: dbData.specifics.scanPatchesSettings.enabled,
      applyPatches: dbData.specifics.installPatchesSettings.enabled,
      patchScopeSecurity: dbData.specifics.installPatchesSettings.updateType.securityPatchesSettings.enabled,
      patchScopeNonSecurity: dbData.specifics.installPatchesSettings.updateType.nonSecurityPatchesSettings.enabled,
      patchScopeManuallyApproved:
        dbData.specifics.installPatchesSettings.updateType.manuallyApprovedPatchesSettings.enabled,
      manuallyApprovedPatchIds: dbData.specifics.installPatchesSettings.manuallyApprovedPatchIds,
      fallbackOnDirectDownload: dbData.specifics.downloadSettings.fallbackOnDirectDownload,
      runOnSystemChanges: dbData.specifics.scanPatchesSettings.runOnSystemChanges,
      schedulingOptions: {
        runAsSoonAsPossible: dbData.specifics.installPatchesSettings.ifMissedRunAsSoonAsPossible,
        useFallbackSchedule: dbData.specifics.scanPatchesSettings.useFallbackSchedule,
        useSameSchedule: dbData.specifics.scanPatchesSettings.useSameSchedule,
      },
      vendorProductsPairsType: dbData.specifics.installPatchesSettings.specificVendorAndProduct.type.toString(),
    };

    if (
      dbData.specifics.installPatchesSettings.specificVendorAndProduct.enabled &&
      dbData.specifics.installPatchesSettings.specificVendorAndProduct.vendorProductsPairs !== undefined
    ) {
      const vendorProductsPairs = dbData.specifics.installPatchesSettings.specificVendorAndProduct.vendorProductsPairs;
      specifics['vendorProductsPairs'] = vendorProductsPairs;
    } else {
      specifics['vendorProductsPairs'] = [];
    }

    const cachingServers = dbData.specifics.downloadSettings.cachingServers;
    specifics['cachingServers'] = cachingServers;

    // reboot options
    if (
      dbData.specifics.installPatchesSettings.postponeReboot !== undefined &&
      dbData.specifics.installPatchesSettings.postponeReboot.option !== undefined
    ) {
      const rebootOptions: MaintenanceWindowRebootOptionsData = {
        rebootOption: dbData.specifics.installPatchesSettings.postponeReboot.option.toString(),
      };
      if (dbData.specifics.installPatchesSettings.postponeReboot.afterMinutes !== undefined) {
        rebootOptions['afterMinutes'] = dbData.specifics.installPatchesSettings.postponeReboot.afterMinutes.toString();
      }
      if (dbData.specifics.installPatchesSettings.postponeReboot.afterCustomMessage !== undefined) {
        rebootOptions['afterCustomMessage'] = dbData.specifics.installPatchesSettings.postponeReboot.afterCustomMessage;
      }
      if (dbData.specifics.installPatchesSettings.postponeReboot.between !== undefined) {
        rebootOptions['between'] = {
          start: {
            hours: dbData.specifics.installPatchesSettings.postponeReboot.between.startHour,
            minutes: dbData.specifics.installPatchesSettings.postponeReboot.between.startMinute,
          },
          end: {
            hours: dbData.specifics.installPatchesSettings.postponeReboot.between.endHour,
            minutes: dbData.specifics.installPatchesSettings.postponeReboot.between.endMinute,
          },
        };
      }

      if (rebootOptions.rebootOption !== PostponeRebootOptions.CUSTOM_TIME.toString()) {
        if (rebootOptions.rebootOption === PostponeRebootOptions.INTERVAL.toString()) {
          rebootOptions['interval'] = {
            minutes: rebootOptions['afterMinutes'],
            customMessage: rebootOptions['afterCustomMessage'],
            between: rebootOptions['between'],
          };
        }

        if (rebootOptions.rebootOption === PostponeRebootOptions.AFTER.toString()) {
          rebootOptions['after'] = {
            minutes: rebootOptions['afterMinutes'],
            customMessage: rebootOptions['afterCustomMessage'],
          };
        }

        delete rebootOptions['afterMinutes'];
        delete rebootOptions['afterCustomMessage'];
        delete rebootOptions['between'];
      }

      specifics['rebootOptions'] = rebootOptions;
    }

    let schedulerForAll: MaintenanceWindowSchedulerSchema,
      schedulerScanningPatches: MaintenanceWindowSchedulerSchema,
      schedulerApplyingPatchesSecurity: MaintenanceWindowSchedulerSchema,
      schedulerApplyingPatchesNonSecurity: MaintenanceWindowSchedulerSchema,
      fallbackSchedulerForAll: MaintenanceWindowSimpleSchedulerSchema,
      fallbackSchedulerScanningPatches: MaintenanceWindowSimpleSchedulerSchema,
      fallbackSchedulerApplyingPatchesSecurity: MaintenanceWindowSimpleSchedulerSchema,
      fallbackSchedulerApplyingPatchesNonSecurity: MaintenanceWindowSimpleSchedulerSchema;

    // scan scheduler
    if (dbData.specifics.scanPatchesSettings.scheduler !== undefined) {
      // do not put default values
      schedulerForAll = schedulerScanningPatches = DbToUiValuesConverter.getSchedulerSettings(
        dbData.specifics.scanPatchesSettings.scheduler
      );
    } else {
      // default values
      schedulerForAll = schedulerScanningPatches = MaintenanceWindowsConstants.scanNewScheduleDefaultValues;
    }

    // security scheduler (if Security not enabled & Manually enabled, then Security scheduler takes Manually values)
    if (!specifics.patchScopeSecurity && specifics.patchScopeManuallyApproved) {
      // use manually approved values
      const manuallyApprovedScheduler =
        dbData.specifics.installPatchesSettings.updateType.manuallyApprovedPatchesSettings.scheduler;
      schedulerApplyingPatchesSecurity =
        manuallyApprovedScheduler !== undefined
          ? DbToUiValuesConverter.getSchedulerSettings(manuallyApprovedScheduler) // do not put default values
          : MaintenanceWindowsConstants.securityNewScheduleDefaultValues; // default values
    } else {
      // otherwise, use security values
      const securityScheduler = dbData.specifics.installPatchesSettings.updateType.securityPatchesSettings.scheduler;
      schedulerApplyingPatchesSecurity =
        securityScheduler !== undefined
          ? DbToUiValuesConverter.getSchedulerSettings(securityScheduler) // do not put default values
          : MaintenanceWindowsConstants.securityNewScheduleDefaultValues; // default values
    }

    // non-security scheduler
    if (dbData.specifics.installPatchesSettings.updateType.nonSecurityPatchesSettings.scheduler !== undefined) {
      // do not put default values
      schedulerApplyingPatchesNonSecurity = DbToUiValuesConverter.getSchedulerSettings(
        dbData.specifics.installPatchesSettings.updateType.nonSecurityPatchesSettings.scheduler
      );
    } else {
      // default values
      schedulerApplyingPatchesNonSecurity = MaintenanceWindowsConstants.securityNewScheduleDefaultValues;
    }

    specifics.schedulingOptions['schedulerForAll'] = schedulerForAll;
    specifics.schedulingOptions['schedulerScanningPatches'] = schedulerScanningPatches;
    specifics.schedulingOptions['schedulerApplyingPatchesSecurity'] = schedulerApplyingPatchesSecurity;
    specifics.schedulingOptions['schedulerApplyingPatchesNonSecurity'] = schedulerApplyingPatchesNonSecurity;

    // fallback scan scheduler
    if (dbData.specifics.scanPatchesSettings.oldScheduler !== undefined) {
      // do not put default values
      fallbackSchedulerForAll = fallbackSchedulerScanningPatches = DbToUiValuesConverter.getFallbackSchedulerSettings(
        dbData.specifics.scanPatchesSettings.oldScheduler
      );
    } else {
      // default values
      fallbackSchedulerForAll = fallbackSchedulerScanningPatches =
        MaintenanceWindowsConstants.scanOldScheduleDefaultValues;
    }

    // fallback security scheduler
    if (!specifics.patchScopeSecurity && specifics.patchScopeManuallyApproved) {
      // use manually approved values
      const manuallyApprovedOldScheduler =
        dbData.specifics.installPatchesSettings.updateType.manuallyApprovedPatchesSettings.oldScheduler;
      fallbackSchedulerApplyingPatchesSecurity =
        manuallyApprovedOldScheduler !== undefined
          ? DbToUiValuesConverter.getFallbackSchedulerSettings(manuallyApprovedOldScheduler) // do not put default values
          : MaintenanceWindowsConstants.securityOldScheduleDefaultValues; // default values
    } else {
      // otherwise, use security values
      const securityOldScheduler =
        dbData.specifics.installPatchesSettings.updateType.securityPatchesSettings.oldScheduler;
      fallbackSchedulerApplyingPatchesSecurity =
        securityOldScheduler !== undefined
          ? DbToUiValuesConverter.getFallbackSchedulerSettings(securityOldScheduler) // do not put default values
          : MaintenanceWindowsConstants.securityOldScheduleDefaultValues; // default values
    }

    // fallback non-security scheduler
    if (dbData.specifics.installPatchesSettings.updateType.nonSecurityPatchesSettings.oldScheduler !== undefined) {
      // do not put default values
      fallbackSchedulerApplyingPatchesNonSecurity = DbToUiValuesConverter.getFallbackSchedulerSettings(
        dbData.specifics.installPatchesSettings.updateType.nonSecurityPatchesSettings.oldScheduler
      );
    } else {
      // default values
      fallbackSchedulerApplyingPatchesNonSecurity = MaintenanceWindowsConstants.nonSecurityOldScheduleDefaultValues;
    }

    specifics.schedulingOptions['fallbackSchedulers'] = {
      schedulerForAll: fallbackSchedulerForAll,
      schedulerScanningPatches: fallbackSchedulerScanningPatches,
      schedulerApplyingPatchesSecurity: fallbackSchedulerApplyingPatchesSecurity,
      schedulerApplyingPatchesNonSecurity: fallbackSchedulerApplyingPatchesNonSecurity,
    };

    const uiData: MaintenanceWindowData = {
      _id: dbData._id,
      name: dbData.name,
      type: dbData.type,
      ownerId: dbData.ownerId,
      allowChangeByOtherUsers: dbData.allowChangeByOtherUsers,
      specifics: specifics,
    };

    return uiData;
  }

  static getSchedulerSettings(dbSchedulerData: any): MaintenanceWindowSchedulerSchema {
    // do not put default values
    const scheduler: MaintenanceWindowSchedulerSchema = {
      recurrence: `${dbSchedulerData.recurrence}`,
      startingWith: dbSchedulerData.startDate + new Date().getTimezoneOffset() * 60,
      between: {
        start: {
          hours: dbSchedulerData.between?.startHour,
          minutes: dbSchedulerData.between?.startMinute,
        },
        end: {
          hours: dbSchedulerData.between?.endHour,
          minutes: dbSchedulerData.between?.endMinute,
        },
      },
    };

    if (scheduler.recurrence !== undefined) {
      scheduler.recurrence = scheduler.recurrence.toString();
    }

    let weeklyRecurrence: WeeklyRecurrence, monthlyRecurrence: MonthlyRecurrence;
    if (dbSchedulerData.weeklyRecurrence !== undefined) {
      weeklyRecurrence = dbSchedulerData.weeklyRecurrence;
      if (weeklyRecurrence.every !== undefined) {
        weeklyRecurrence.every = weeklyRecurrence.every.toString();
      }
      scheduler['weeklyRecurrence'] = weeklyRecurrence;
    }

    if (dbSchedulerData.monthlyRecurrence !== undefined) {
      monthlyRecurrence = dbSchedulerData.monthlyRecurrence;
      // parse int to strings for dropdowns
      if (monthlyRecurrence.every !== undefined) {
        monthlyRecurrence.every = monthlyRecurrence.every.toString();
      }
      if (monthlyRecurrence.frequency !== undefined) {
        monthlyRecurrence.frequency = monthlyRecurrence.frequency.toString();
      }
      if (monthlyRecurrence.frequencyDayOfWeek !== undefined) {
        monthlyRecurrence.frequencyDayOfWeek = monthlyRecurrence.frequencyDayOfWeek.toString();
      }
      scheduler['monthlyRecurrence'] = monthlyRecurrence;
    }

    return scheduler;
  }

  static getFallbackSchedulerSettings(dbSchedulerData: any): MaintenanceWindowSimpleSchedulerSchema {
    // do not put default values
    const scheduler: MaintenanceWindowSimpleSchedulerSchema = {
      recurrence: dbSchedulerData.recurrence,
    };

    if (scheduler.recurrence !== undefined) {
      scheduler.recurrence = scheduler.recurrence.toString();
    }

    if (dbSchedulerData.recurrence === RecurrenceOptions.DAILY) {
      scheduler['daily'] = {
        at: {
          hours: dbSchedulerData.startHour,
          minutes: dbSchedulerData.startMinute,
        },
      };
    }

    if (dbSchedulerData.recurrence === RecurrenceOptions.WEEKLY) {
      scheduler['weekly'] = {
        at: {
          hours: dbSchedulerData.startHour,
          minutes: dbSchedulerData.startMinute,
        },
        days: dbSchedulerData.days,
      };
    }

    return scheduler;
  }
}
